import { defineStore } from "pinia";
import { Category, Client, Field, Group, Label, Status, User } from "@/types";
import { UserService } from "@/services/user";
import storage from "@/utils/storage";
import { betaClientId } from "@/utils/indicators";
import mixpanelTracker from "@/utils/mixpanel-tracker";
import { useFabriqStore } from "@/store/fabriq";

interface ClientState {
  client: any;
  categories: Category[];
  custom_fields: Partial<Record<"custom_1" | "custom_2", Record<string, any>>>;
  extra_fields: Field[];
  groups: Group[];
  labels: Label[];
  statuses: Status[];
  users: User[];
  locale: string;
  fields: Record<string, Field[]>;
  config: any;
  diamondUserMapping: {
    future_user_id: string;
    user_id: number;
  }[];
}

export const useClientStore = defineStore("client", {
  state: (): ClientState => {
    return {
      client: null,
      categories: [],
      custom_fields: {},
      extra_fields: [],
      groups: [],
      labels: [],
      statuses: [],
      users: [],
      fields: {},
      locale: "",
      config: {},
      diamondUserMapping: [],
    };
  },
  actions: {
    setClient(client: Client) {
      // eslint-disable-next-line fabriq-webapp/vue-composable-rules -- we're in a store
      const fabriqStore = useFabriqStore();
      this.locale = fabriqStore.locale;
      this.categories = client.categories;
      this.custom_fields = client.custom_fields;
      this.extra_fields = client.extra_fields;
      // Filter out churned groups temporarily until we
      // have a better solution applied in the backend
      this.groups = client.groups.filter((g) => g.status !== "churned") || [];
      this.labels = client.labels || [];
      this.statuses = client.statuses || [];
      this.users = client.users || [];
      this.config = client.config || {};
      this.client = {
        ...client,
        categories: undefined,
        custom_fields: undefined,
        extra_fields: undefined,
        groups: undefined,
        labels: undefined,
        statuses: undefined,
        users: undefined,
        config: undefined,
      };
      this.setTranslations();
      storage.writeToStorage("client", client);
      mixpanelTracker.setClient(client);
    },
    async load(): Promise<Client | undefined> {
      try {
        const client = await UserService.getClient();
        const diamondUserMapping = await UserService.getDiamondUserMapping();
        this.diamondUserMapping = diamondUserMapping;
        this.setClient(client);
        return client;
      } catch (e) {
        console.error(e);
      }
    },
    setTranslations(locale?: string) {
      return new Promise(() => {
        this.categories = <Category[]>this.translate(this.categories, locale);
        this.statuses = <Status[]>this.translate(this.statuses, locale);
        this.labels = <Label[]>this.translate(this.labels, locale);
      });
    },
    translate(items: (Category | Status | Label)[], locale?: string) {
      const currentLocale = locale || this.locale;
      return items.map((item) => {
        const originalName = item.originalName || item.name;
        return {
          ...item,
          originalName,
          name: item.config?.i18n?.hasOwnProperty(currentLocale)
            ? item.config?.i18n[currentLocale]
            : originalName,
        };
      });
    },
    async logout() {
      this.client = null;
    },

    findUserByFutureUserId(future_user_id: string): User | undefined {
      return this.usersWithFutureUserId.find(
        (u) => u.future_user_id === future_user_id
      );
    },

    setCategories(categories: Category[]) {
      this.categories = categories;
    },
    setStatuses(statuses: Status[]) {
      this.statuses = statuses;
    },
    setLabels(labels: Label[]) {
      this.labels = labels;
    },
    setExtraFields(fields: Field[]) {
      this.extra_fields = fields;
    },
    setGroups(groups: Group[]) {
      this.groups = groups;
    },
  },
  getters: {
    activeUsers(): User[] {
      return this.users.filter((user) => user.is_active);
    },
    hasKpi(): boolean {
      if (!this.client?.id) return false;
      if (betaClientId.includes(this.client?.id)) return true;
      return !!this.config?.hasMobileKpi;
    },

    mobileScreenRotationEnabled(): boolean {
      return this.config?.mobileScreenRotationEnabled || false;
    },
    usersWithFutureUserId(): (User & {
      future_user_id: string;
      fullName: string;
    })[] {
      const users: Array<User & { fullName: string; future_user_id: string }> =
        [];
      this.users.forEach((u: User) => {
        const fullName = String(`${u.first_name} ${u.last_name}`);
        const future_user_id =
          this.diamondUserMapping.find((fu) => fu.user_id === u.id)
            ?.future_user_id ?? "";
        if (!future_user_id.length) return;
        users.push({ ...u, future_user_id, fullName });
      });
      users.sort((a, b) => a.fullName.localeCompare(b.fullName));
      return users;
    },
    tmpNewTeamRoles(): boolean {
      return this.config?.tmpNewTeamRoles || false;
    },
    allFields(): Field[] {
      const allFieldsWithDuplicates = this.groups.flatMap(
        (group) => group.extra_fields
      );
      const uniqueIds = [
        ...new Set(allFieldsWithDuplicates.map((field) => field.id)),
      ];
      return uniqueIds
        .map((id) => allFieldsWithDuplicates.find((f) => f.id === id))
        .filter(Boolean) as Field[];
    },
    allZones(): Field[] {
      return [
        ...this.groups.flatMap((group: Group) =>
          group.extra_fields.filter((v: Field) => v.type === "zone")
        ),
        ...this.extra_fields.filter((v: Field) => v.type === "zone"),
      ];
    },
  },
});
